import { useQueryClient } from '@tanstack/react-query';

import { useToasts } from '../../../cdl/Toast/useToasts';
import { queries } from '../../api/queryKeys/queries';
import { translate } from '../../helpers/translate.helper';
import { useGroupContext } from '../../hooks/useGroupContext';
import { GroupContextType } from '../../utils/groupContextType';
import { isCustomerContextType } from '../../utils/groupContextTypeUtils';
import { CommandListNestedRouterPathEnum } from '../components/commandRoutes';

import { useShortcutMenu } from './useShortcutMenu';

export const useGroupContextSelectionHandler = () => {
    const { setOpen, setPages, setSearch, setGroupContextActionType } = useShortcutMenu();
    const { addErrorToast } = useToasts();
    const { setGroupContext } = useGroupContext();
    const queryClient = useQueryClient();

    const openNavigateMenu = () => {
        setSearch('');
        setPages([CommandListNestedRouterPathEnum.NAVIGATE]);
        setGroupContextActionType(null);
    };

    const handleError = () => {
        addErrorToast(translate('global.genericError'));
        setOpen(false);
        setSearch('');
        setPages([]);
        setGroupContextActionType(null);
    };

    return {
        selectGroup: async ({
            groupId,
            type,
        }: {
            groupId: string;
            type: GroupContextType.CUSTOMER_GROUP | GroupContextType.SUPPLIER_GROUP;
        }) => {
            try {
                if (isCustomerContextType(type)) {
                    await queryClient.prefetchQuery(queries.customerGroups.detail(groupId));
                } else {
                    await queryClient.prefetchQuery(queries.supplierGroups.detail(groupId));
                }

                if (isCustomerContextType(type)) {
                    const customers = await queryClient.fetchQuery(queries.customers.list({ groupId }));
                    setGroupContext({
                        groupId,
                        companyIds: customers.map((customer) => customer.id),
                        type,
                    });
                } else {
                    const suppliers = await queryClient.fetchQuery(queries.suppliers.list({ groupId }));
                    setGroupContext({
                        groupId,
                        companyIds: suppliers.map((supplier) => supplier.id),
                        type,
                    });
                }
                openNavigateMenu();
            } catch {
                handleError();
            }
        },
        selectCompany: async ({
            companyId,
            groupId,
            type,
        }: {
            companyId: string;
            groupId: string;
            type: GroupContextType.CUSTOMER_COMPANY | GroupContextType.SUPPLIER_COMPANY;
        }) => {
            try {
                if (isCustomerContextType(type)) {
                    await queryClient.prefetchQuery(queries.customers.detail(companyId));
                } else {
                    await queryClient.prefetchQuery(queries.suppliers.detail(companyId));
                }
                setGroupContext({ groupId, companyIds: [companyId], type });
                openNavigateMenu();
            } catch {
                handleError();
            }
        },
    };
};
